import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { EditHouseModal } from "@features/EditHouseModal/EditHouseModal";
import { JointBillTable } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/JointBillTable/JointBillTable";
import { OpenPeriodModal } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/components/OpenPeriodModal/OpenPeriodModal";
import { SelectPlanModal } from "@features/SelectPlanModal/SelectPlanModal";
import { useModalStore } from "@store/modal/modal";
import { prefetchAddress } from "@services/adresses/queries";
import { useSelectedJointBillServiceProviderBillPlansQuery } from "@services/jointBillServiceProviderBillPlans/queries";
import { PlanId } from "@services/jointBillServiceProviderBillPlans/types";
import {
  useGetJointBillServicePeriodPdfMutation,
  useGetJointBillServicePeriodProjectPdfMutation,
} from "@services/jointBillServices/mutations";
import { prefetchJointBillServiceEditDataQuery } from "@services/jointBillServices/queries";
import { type IJointBillServiceEditData } from "@services/jointBillServices/types";
import {
  type IJointBillServicePeriodData,
  type IOverviewJointBillService,
  type IOverviewOtherUserData,
  type SelectionState,
} from "@services/overview/types";
import { downloadFile, isNullOrUndefined } from "@utils/common";
import { plansMetadata } from "@utils/plans/constants";
import { queryClient } from "@/queryClient";

interface IJointBillProps {
  jointBillService: IOverviewJointBillService;
  periodData: IJointBillServicePeriodData;
  periodId: number;
  otherUsers?: IOverviewOtherUserData[];
  jointBillSelectionState?: SelectionState;
  isJointBillCheckboxDisabled?: boolean;
  isJointBillCheckboxLoading?: boolean;
  onJointBillCheckboxClick?: () => void;
  orderId: number;
}

export const JointBill: React.FC<IJointBillProps> = ({
  jointBillService,
  periodData,
  periodId,
  otherUsers,
  jointBillSelectionState,
  isJointBillCheckboxDisabled,
  isJointBillCheckboxLoading,
  onJointBillCheckboxClick,
  orderId,
}) => {
  const [searchParams] = useSearchParams();
  const openJointBillServiceIds = searchParams
    .getAll("open")
    .map((id) => parseInt(id));
  const { t } = useTranslation();
  const { create } = useModalStore();
  const { jointBillServiceId, displayName } = jointBillService;
  const { data: planData } =
    useSelectedJointBillServiceProviderBillPlansQuery();
  const { mutateAsync: getPdf, isPending: isPdfLoading } =
    useGetJointBillServicePeriodPdfMutation();
  const { mutateAsync: getProjectPdf, isPending: isProjectPdfLoading } =
    useGetJointBillServicePeriodProjectPdfMutation();
  const [isHouseEditPrefetchLoading, setIsHouseEditPrefetchLoading] =
    useState(false);

  const billData = (periodData?.billData ?? []).sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );
  const donationData = periodData?.donationData ?? [];

  const selectedPlanId =
    planData?.jointBillServicePlans.find(
      (plan) => plan.jointBillServiceId === jointBillServiceId,
    )?.jointBillServiceProviderBillPlanId ?? PlanId.FRE;

  const planName = t(plansMetadata[selectedPlanId].name);

  const providerBillPlanId =
    periodData.jointBillServiceProviderBillPlanId ?? PlanId.FRE;

  const providerBillPlanName = t(plansMetadata[providerBillPlanId]?.name);

  const isBillDefaultOpen = openJointBillServiceIds.length
    ? openJointBillServiceIds.some((jbs) => jbs === jointBillServiceId)
    : true;

  const openSelectPlanModal = () =>
    create((modalProps) => (
      <SelectPlanModal
        {...modalProps}
        jointBillServiceId={jointBillServiceId}
      />
    ));

  const downloadPdf = () => {
    toast.promise(getPdf({ jointBillServiceId, periodId }), {
      loading: t("bill.interactions.downloadBill.loading"),
      success: (file) => {
        if (!isNullOrUndefined(file?.data)) {
          downloadFile(file);
        }
        return t("bill.interactions.downloadBill.success");
      },
      error: t("bill.interactions.downloadBill.error"),
    });
  };

  const downloadProjectPdf = () => {
    toast.promise(getProjectPdf({ jointBillServiceId, periodId }), {
      loading: t("bill.interactions.downloadProjectBill.loading"),
      success: (file) => {
        if (!isNullOrUndefined(file?.data)) {
          downloadFile(file);
        }
        return t("bill.interactions.downloadProjectBill.success");
      },
      error: t("bill.interactions.downloadProjectBill.error"),
    });
  };

  const prefetchHouseData = async () => {
    setIsHouseEditPrefetchLoading(true);
    await prefetchJointBillServiceEditDataQuery(jointBillServiceId);
    const houseData: IJointBillServiceEditData | undefined =
      await queryClient.getQueryData([
        "jointBillServiceEditData",
        jointBillServiceId,
      ]);

    if (!isNullOrUndefined(houseData)) {
      await prefetchAddress(
        houseData?.address.municipalityId,
        houseData?.address.settlementId,
      );
    }

    setIsHouseEditPrefetchLoading(false);
  };

  const prefetchHouseAndOpenHouseEditModal = () => {
    toast.promise(prefetchHouseData, {
      loading: t("bill.interactions.editAddress.loading"),
      finally: () => {
        create((modalProps) => (
          <EditHouseModal
            {...modalProps}
            jointBillServiceId={jointBillServiceId}
          />
        ));
      },
    });
  };

  const openPeriodOpenModal = () =>
    create((modalProps) => (
      <OpenPeriodModal
        {...modalProps}
        jointBillServiceId={jointBillServiceId}
        periodId={periodId}
      />
    ));

  return (
    <JointBillTable
      jointBillServiceId={jointBillServiceId}
      orderId={orderId}
      periodId={periodId}
      houseDisplayName={displayName ?? t("common.defaultAddress")}
      otherUsers={otherUsers}
      planName={planName}
      periodState={periodData.jointBillServicePeriodState}
      paidAmount={periodData.paidAmount}
      totalSum={periodData.totalSum}
      totalAmount={periodData.totalAmount}
      totalExpectedPayment={periodData.totalExpectedPayment}
      unpaidAmount={periodData.unpaidAmount}
      onChangePlanClick={openSelectPlanModal}
      isEditHouseLoading={isHouseEditPrefetchLoading}
      onEditHouseClick={prefetchHouseAndOpenHouseEditModal}
      bills={billData}
      donations={donationData}
      jointBillSelectionState={jointBillSelectionState}
      isJointBillCheckboxDisabled={isJointBillCheckboxDisabled}
      isJointBillCheckboxLoading={isJointBillCheckboxLoading}
      onJointBillCheckboxClick={onJointBillCheckboxClick}
      isBillDownloadEnabled={periodData?.isPdfFileDownloadAvailable}
      onBillDownloadClick={downloadPdf}
      isBillDownloadLoading={isPdfLoading}
      projectTypeId={jointBillService.projectTypeId}
      isProjectBillDownloadEnabled={periodData?.projectBillPdfDownloadable}
      onProjectBillDownloadClick={downloadProjectPdf}
      isProjectBillDownloadLoading={isProjectPdfLoading}
      isOpenPeriodButtonEnabled={periodData.canOpenPeriod}
      onOpenPeriodButtonClick={openPeriodOpenModal}
      isBillDefaultOpen={isBillDefaultOpen}
      canBePaid={periodData.canBePaid}
      providerBillPlanName={providerBillPlanName}
      requestedSpBillData={periodData.requestedServiceProviderBillData}
      isServiceProviderCategorySuggestionEnabled={
        periodData.displayServiceProviderSuggestions
      }
      vouchers={periodData.vouchers}
    />
  );
};
